import React, { useState } from 'react';
import ChordLine from './ChordLine';
import TabBuilder from './TabBuilder'; // Ensure you created this file from the previous step
import './SongSection.css'; 

const SongSection = ({ section, onUpdate, onDelete, onMove, index, totalSections }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [showTabBuilder, setShowTabBuilder] = useState(false);
  const [copyStatus, setCopyStatus] = useState('');

  const handleTextChange = (e) => {
    onUpdate({ ...section, content: e.target.value });
  };

  const handleLineUpdate = (lineIndex, newLine) => {
    const lines = (section.content || "").split('\n');
    lines[lineIndex] = newLine;
    onUpdate({ ...section, content: lines.join('\n') });
  };

  const toggleEditing = () => {
    const nextEditing = !isEditing;
    setIsEditing(nextEditing);
    setShowTabBuilder(nextEditing && Boolean(section.tab));
  };

  const getPlainLyrics = () => {
    return (section.content || "")
      .split('\n')
      .map(line => line.replace(/\[.*?\]/g, '').replace(/[ \t]+/g, ' ').trim())
      .join('\n')
      .trim();
  };

  const copyLyrics = async () => {
    const plainLyrics = getPlainLyrics();
    if (!plainLyrics) return;

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(plainLyrics);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = plainLyrics;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopyStatus('Copied');
      setTimeout(() => setCopyStatus(''), 1600);
    } catch (err) {
      console.error(err);
      setCopyStatus('Copy failed');
      setTimeout(() => setCopyStatus(''), 2000);
    }
  };

  // Helper: Renders the JSON tab data as clean, wrapped ASCII text
  const renderASCIITab = (jsonTab) => {
    if (!jsonTab) return null;
    
    let grid;
    try {
      grid = JSON.parse(jsonTab);
    } catch (e) { return null; }

    const strings = ['e', 'B', 'G', 'D', 'A', 'E'];
    const STEPS_PER_SYSTEM = 16; // Must match your TabBuilder setting
    const totalCols = grid[0].length;
    const totalSystems = Math.ceil(totalCols / STEPS_PER_SYSTEM);
    
    return (
      <div className="ascii-tab-container">
        {Array.from({ length: totalSystems }).map((_, systemIndex) => (
          <div key={systemIndex} className="ascii-system-block">
            {grid.map((row, i) => (
              <div key={i} className="ascii-row">
                <span className="ascii-string">{strings[i]}|</span>
                {/* Slice the row for this specific system/line */}
                {row.slice(systemIndex * STEPS_PER_SYSTEM, (systemIndex + 1) * STEPS_PER_SYSTEM)
                    .map((cell, c) => {
                      const val = cell || "-";
                      return <span key={c}>{val}-</span>;
                    })
                }
                |
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="section-block">
      {/* --- SECTION HEADER --- */}
      <div className="section-header">
        <div className="header-left">
          <input 
            className="section-title-input"
            value={section.title}
            onChange={(e) => onUpdate({ ...section, title: e.target.value })}
            placeholder="Section Title"
          />
          <input 
            className="section-strum-input"
            value={section.strumming || ""}
            onChange={(e) => onUpdate({ ...section, strumming: e.target.value })}
            placeholder="Strum: ↓ ↓↑ ↓↑"
          />
        </div>

        <div className="section-controls">
          {/* Reorder Buttons */}
          <div className="move-btn-group">
            <button 
              className="btn-circle-move" 
              onClick={() => onMove(-1)} 
              disabled={index === 0}
              title="Move Up"
            >
              ↑
            </button>
            <button 
              className="btn-circle-move" 
              onClick={() => onMove(1)} 
              disabled={index === totalSections - 1}
              title="Move Down"
            >
              ↓
            </button>
          </div>

          <button 
            className="btn-text-toggle" 
            onClick={toggleEditing}
          >
            {isEditing ? "Done" : "✎ Edit"}
          </button>

          {!isEditing && (
            <button
              className="btn-text-toggle"
              onClick={copyLyrics}
              disabled={!getPlainLyrics()}
              title="Copy lyrics without chord markers"
            >
              {copyStatus || "Copy Lyrics"}
            </button>
          )}
          
          <button className="btn-delete-section" onClick={onDelete}>X</button>
        </div>
      </div>

      {/* --- EDIT MODE --- */}
      {isEditing ? (
        <>
          <textarea
            className="lyric-editor-textarea"
            value={section.content}
            onChange={handleTextChange}
            placeholder="Paste lyrics here..."
            rows={(section.content || "").split('\n').length + 2}
          />
          
          {/* TAB EDITOR AREA */}
          <div style={{marginTop: '15px', borderTop:'1px dashed #eee', paddingTop:'15px'}}>
             {!showTabBuilder ? (
               <button 
                 className="btn-secondary" 
                 style={{padding:'8px 12px', fontSize:'0.85rem', background:'#6c757d'}}
                 onClick={() => setShowTabBuilder(true)}
               >
                 {section.tab ? "🎸 Edit Guitar Riff" : "🎸 Add Guitar Riff"}
               </button>
             ) : (
               <TabBuilder 
                 initialData={section.tab} 
                 onCancel={() => setShowTabBuilder(false)}
                 onSave={(newTabData) => {
                    onUpdate({ ...section, tab: newTabData });
                    setShowTabBuilder(false);
                 }}
               />
             )}
             
             {/* Small Preview while editing lyrics */}
             {section.tab && !showTabBuilder && (
                <div style={{opacity: 0.6, transform:'scale(0.9)', transformOrigin:'left top', marginTop:'10px'}}>
                   {renderASCIITab(section.tab)}
                </div>
             )}
          </div>
        </>
      ) : (
        // --- INTERACTIVE / VIEW MODE ---
        <div className="interactive-view">
          {(section.content || "").split('\n').map((line, idx) => (
            <ChordLine key={idx} text={line} onChange={(newLine) => handleLineUpdate(idx, newLine)} />
          ))}
          {!section.content && (
            <div className="empty-state-msg" onClick={() => setIsEditing(true)}>
              (No lyrics yet. Click "Edit" to add text)
            </div>
          )}
          
          {/* RENDER THE TAB */}
          {section.tab && (
            <div style={{opacity: 0.6, transform:'scale(0.9)', transformOrigin:'left top', marginTop:'25px'}}>
               {renderASCIITab(section.tab)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SongSection;
